import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';

// Create the context
const AiCommandPaletteContext = createContext(null);

// Message types 
const MessageTypes = {
  USER: 'user',
  AI: 'ai'
};

// Context provider component
export const AiCommandPaletteProvider = ({ children, isOpen, onClose }) => {
  // Input and search state
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  
  // Default suggestions
  const [defaultSuggestions, setDefaultSuggestions] = useState([
    "What tasks need my attention?",
    "What should I work on next?",
    "Can you summarize my week?"
  ]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  
  // Context indicator state
  const [contextIndicator, setContextIndicator] = useState(null);
  
  // AI-related state
  const [aiResults, setAiResults] = useState(null);
  const [isAskMode, setIsAskMode] = useState(false);
  const [currentQuery, setCurrentQuery] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('Gathering context from your organizations');
  
  // Conversation state
  const [conversationHistory, setConversationHistory] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  
  // UI state
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(true);
  const [error, setError] = useState(null);
  
  // Refs
  const inputRef = useRef(null);
  
  // Hooks
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();

  // Generate context-specific suggestions
  const generateContextSuggestions = useCallback((context) => {
    if (!context) {
      return [
        "What needs my attention?",
        "Any overdue tasks?",
        "How was my week?"
      ];
    }

    switch (context.type) {
      case 'task':
        return [
          `Is ${context.label} done?`,
          `Summarize the activity on ${context.label}`,
          `Find tasks similar to ${context.label}`
        ];
      case 'project':
        return [
          `Show tasks in ${context.label}`,
          `Summarize the last week in ${context.label}`,
          `Anything waiting on me in ${context.label}?`
        ];
      case 'user':
        return [
          `What's assigned to ${context.label}?`,
          `Show completed tasks by ${context.label}`,
          `What's ${context.label} working on?`
        ];
      case 'organization':
        return [
          `Show active projects in ${context.label}`,
          `Summarize the last week in ${context.label}`,
          `Anything waiting on me in ${context.label}?`
        ];
      case 'today':
        return [
          `What should I work on today?`,
          `What is due soon?`,
          `Any thing waiting on me?`
        ];
      default:
        return [
          "What needs my attention?",
          "Any overdue tasks?",
          "How was my week?"
        ];
    }
  }, []);

  // Update suggestions when context changes
  useEffect(() => {
    const newSuggestions = generateContextSuggestions(contextIndicator);
    setDefaultSuggestions(newSuggestions);
  }, [contextIndicator, generateContextSuggestions]);

  // Reset state when closed
  useEffect(() => {
    if (!isOpen) {
      setInputValue('');
      setSearchResults([]);
      setAiResults(null);
      setSelectedIndex(-1);
      setIsLoading(false);
      setLoadingMessage('Gathering context from your organizations');
      setError(null);
      setIsAskMode(false);
      setCurrentQuery('');
      setConversationHistory([]);
      setShowSuggestions(true);
      // Don't reset context indicator when closing
    } else {
      // When opening, refresh suggestions based on current context
      const newSuggestions = generateContextSuggestions(contextIndicator);
      setDefaultSuggestions(newSuggestions);
      setShowSuggestions(true);
    }
  }, [isOpen, generateContextSuggestions, contextIndicator]);

  // Generate a new conversation ID if needed
  useEffect(() => {
    if (!conversationId) {
      setConversationId(`conv-${Date.now()}`);
    }
  }, [conversationId]);

  // Auto-focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle input change with context processing
  const handleInputChange = useCallback((value) => {
    setInputValue(value);
    // Hide suggestions when the user starts typing
    if (value.trim() !== '') {
      setShowSuggestions(false);
    } else {
      setShowSuggestions(true);
    }
  }, []);

  // Apply a suggestion to the input
  const applySuggestion = useCallback((suggestion) => {
    setInputValue(suggestion);
    setShowSuggestions(false);
    // Focus the input after applying a suggestion
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Set context from current page/task
  const setContextFromPage = useCallback((context) => {
    if (context) {
      console.log('Setting context from page:', context); 
      setContextIndicator({
        type: context.type,
        label: context.label,
        data: context.data
      });
    } else {
      setContextIndicator(null);
    }
  }, []);

  // Remove the context indicator
  const removeContext = useCallback(() => {
    setContextIndicator(null);
  }, []);

  // Search functionality
  const handleSearch = useCallback(async () => {
    if (!inputValue.trim()) return;
    
    try {
      const response = await axiosInstance.post('/ai-search/typeahead', {
        query: inputValue,
        context: contextIndicator
      });
      setSearchResults(response.data.results);
      setAiResults(null);
      setError(null);
      
      // Only auto-select the AI option if there are no search results
      if (response.data.results.length === 0) {
        setSelectedIndex(0); // Select AI option
      } else {
        setSelectedIndex(-1); // No selection by default
      }
    } catch (error) {
      console.error('Search error:', error);
      setError('An error occurred while searching. Please try again.');
      setSearchResults([]);
      setSelectedIndex(-1);
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, axiosInstance, contextIndicator]);

  // Add a message to the conversation history
  const addMessageToHistory = useCallback((text, type, content = null) => {
    setConversationHistory(prev => [
      ...prev,
      {
        id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        text,
        isUser: type === MessageTypes.USER,
        timestamp: new Date(),
        content
      }
    ]);
  }, []);

  // Start a new conversation
  const startNewConversation = useCallback(() => {
    setConversationId(`conv-${Date.now()}`);
    setConversationHistory([]);
    setAiResults(null);
  }, []);

  // AI ask functionality with conversation support
  const handleAiAsk = useCallback(async (providedQuery = null) => {
    // Use provided query if available, otherwise use input value
    const query = providedQuery || inputValue.trim();
    
    if (!query) return;
    
    // Store the query before clearing the input
    setCurrentQuery(query);
    
    // Clear the input and set loading state
    setInputValue('');
    setIsLoading(true);
    
    // Set ask mode immediately when AI is triggered
    setIsAskMode(true);
    
    // Add user message to history after setting loading state
    addMessageToHistory(query, MessageTypes.USER);
    
    try {
      const response = await axiosInstance.post('/ai-search/conversation', {
        query,
        conversationHistory,
        context: contextIndicator
      });

      if (response.data.success) {
        setError(null);
        if (response.data.type === 'search_results') {
          setSearchResults(response.data.data);
          setAiResults(null);
        } else if (response.data.type === 'ai_response') {
          const aiData = response.data.data;
          setAiResults(aiData);
          setSearchResults([]);
          
          // Add AI response to conversation history
          addMessageToHistory('', MessageTypes.AI, aiData);
        }
      } else {
        throw new Error('AI response was not successful');
      }
    } catch (error) {
      console.error('AI ask error:', error);
      setError('An error occurred while processing your request. Please try again.');
      setAiResults(null);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
      // Clear current query after loading is complete
      setCurrentQuery('');
      
      // Refocus the input after the AI query processing is complete
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [inputValue, axiosInstance, conversationHistory, addMessageToHistory, MessageTypes, contextIndicator, inputRef]);

  // Debounced search effect
  useEffect(() => {
    // Skip if in ask mode or if input starts with /, we don't want to trigger search then
    if (isAskMode || inputValue.startsWith('/')) return;
    
    if (!inputValue.trim()) {
      setSearchResults([]);
      setSelectedIndex(-1);
      setIsLoading(false); // Reset loading state when input is cleared
      return;
    }

    setIsLoading(true);
    // No longer auto-selecting the AI option by default
    setSelectedIndex(-1);

    const timer = setTimeout(() => {
      if (inputValue && !inputValue.startsWith('/')) {
        handleSearch();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue, handleSearch, isAskMode]);

  // Keyboard navigation with context handling
  const handleKeyDown = useCallback((e) => {
    // Special handling for backspace to remove context
    if (e.key === 'Backspace' && contextIndicator && inputValue === '') {
      e.preventDefault();
      removeContext();
      return;
    }

    if (e.key === 'Escape') {
      if (isAskMode) {
        setIsAskMode(false);
        setAiResults(null);
        setConversationHistory([]);
        setInputValue('');
      } else {
        onClose();
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      
      // Special handling for context commands that start with /
      if (inputValue.startsWith('/')) {
        // Extract the context value - everything after the slash up to the first space or end
        const contextValue = inputValue.includes(' ') 
          ? inputValue.substring(1, inputValue.indexOf(' ')).trim()
          : inputValue.substring(1).trim();

        // Make sure we have a valid context
        if (contextValue) {
          setContextIndicator({
            type: 'manual',
            value: contextValue,
            label: contextValue
          });
          
          // If there's text after the context, set it as input
          if (inputValue.includes(' ')) {
            setInputValue(inputValue.substring(inputValue.indexOf(' ')).trim());
          } else {
            setInputValue('');
          }
          return;
        }
      }
      
      if (isAskMode) {
        // In ask mode, hitting enter with text triggers a follow-up question
        if (inputValue.trim()) {
          handleAiAsk();
        }
      } else {
        // In search mode
        if (selectedIndex === -1) {
          // If nothing is selected but we have text, treat it as AI query
          if (inputValue.trim()) {
            handleAiAsk();
          }
        } else if (selectedIndex === searchResults.length) {
          // If the AI option is selected (now at the end), trigger AI response
          handleAiAsk();
        } else if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          // If a search result is selected, navigate to it
          const selectedTask = searchResults[selectedIndex];
          if (selectedTask.organization?.slug && selectedTask.key) {
            navigate(`/${selectedTask.organization.slug}/plan/${selectedTask.key}`);
            onClose();
          }
        }
      }
    } else if (inputValue.trim()) {
      const totalOptions = searchResults.length + 1; // +1 for AI option
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => {
            if (prev === -1) return 0; // First item if nothing selected
            return prev < totalOptions - 1 ? prev + 1 : prev;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => {
            if (prev === -1) return totalOptions - 1; // Last item if nothing selected
            return prev > 0 ? prev - 1 : prev;
          });
          break;
        case 'Tab':
          e.preventDefault();
          setSelectedIndex(prev => {
            if (prev === -1) {
              return e.shiftKey ? totalOptions - 1 : 0;
            }
            return e.shiftKey
              ? (prev > 0 ? prev - 1 : totalOptions - 1)
              : (prev < totalOptions - 1 ? prev + 1 : 0);
          });
          break;
      }
    }
  }, [isAskMode, inputValue, selectedIndex, searchResults, handleAiAsk, navigate, onClose, contextIndicator, removeContext]);

  // Status icon helper
  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case 'done':
        return 'done';
      case 'in_progress':
        return 'in_progress';
      default:
        return 'todo';
    }
  }, []);

  // Overlay click handler
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Loading message cycling effect
  useEffect(() => {
    if (!isLoading || !aiResults) return;

    const messages = [
      'Gathering context from your organizations',
      'Crunching data just for you',
      'Processing and generating a response'
    ];
    let index = 0;

    const interval = setInterval(() => {
      if (index < messages.length - 1) {
        index++;
        setLoadingMessage(messages[index]);
      }
    }, 3000);

    setLoadingMessage(messages[0]);

    return () => {
      clearInterval(interval);
      if (index < messages.length - 1) {
        const quickCycle = async () => {
          for (let i = index + 1; i < messages.length; i++) {
            setLoadingMessage(messages[i]);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        };
        quickCycle();
      }
    };
  }, [isLoading, aiResults]);

  // UI control callbacks
  const handleCloseKeyboardShortcuts = useCallback(() => {
    setShowKeyboardShortcuts(false);
  }, []);

  const handleBackToSearch = useCallback(() => {
    setIsAskMode(false);
    setAiResults(null);
    setInputValue('');
    setCurrentQuery('');
    // Also clear conversation history when going back to search
    setConversationHistory([]);
    inputRef.current?.focus();
  }, []);

  // Navigate to a task 
  const handleNavigateToTask = useCallback((task) => {
    if (task.organization?.slug && task.key) {
      navigate(`/${task.organization.slug}/plan/${task.key}`);
      onClose();
    }
  }, [navigate, onClose]);

  // Handle link click
  const handleLinkClick = useCallback((url) => {
    if (url) {
      navigate(url);
      onClose();
    }
  }, [navigate, onClose]);

  // Context value
  const value = {
    // State
    inputValue,
    setInputValue,
    handleInputChange,
    isLoading,
    searchResults,
    aiResults,
    isAskMode,
    currentQuery,
    selectedIndex,
    setSelectedIndex,
    loadingMessage,
    showKeyboardShortcuts,
    error,
    inputRef,
    conversationHistory,
    conversationId,
    contextIndicator,
    defaultSuggestions,
    showSuggestions,
    
    // Actions
    handleSearch,
    handleAiAsk,
    handleKeyDown,
    getStatusIcon,
    handleOverlayClick,
    handleCloseKeyboardShortcuts,
    handleBackToSearch,
    handleNavigateToTask,
    handleLinkClick,
    startNewConversation,
    addMessageToHistory,
    setContextFromPage,
    removeContext,
    applySuggestion,
    generateContextSuggestions,
    
    // Constants
    MessageTypes,
    
    // Control props
    isOpen,
    onClose
  };

  return (
    <AiCommandPaletteContext.Provider value={value}>
      {children}
    </AiCommandPaletteContext.Provider>
  );
};

// Custom hook to use the context
export const useAiCommandPalette = () => {
  const context = useContext(AiCommandPaletteContext);
  if (!context) {
    throw new Error('useAiCommandPalette must be used within an AiCommandPaletteProvider');
  }
  return context;
}; 